import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { config } from 'src/app/config/api.config';
import { WebinarSimplifyDto } from 'src/app/model/webinar-simplify.dto';
import { CustomResponse } from '../ami/service/ami.service';
import { Observable } from 'rxjs';
import { PaginatedResponse } from 'src/app/interfaces/paginateResponse.interface';

/** Rôle attendu par l'API intervenants (le back accepte aussi "Moderateur" / "Animateur"). */
export type IntervenantRole = 'MODERATOR' | 'PANELISTE';

/** Corps de POST /intervenants (IntervenantRequestDto côté back). */
export interface IntervenantRequest {
  email: string;
  firstName: string;
  lastName: string;
  role: IntervenantRole | string;
  civility?: string;
}

/** Réponse de GET /intervenants (IntervenantResponseDto côté back). */
export interface IntervenantResponse {
  email: string;
  civility?: string | null;
  firstName: string;
  lastName: string;
  role: string; // "Moderateur" | "Animateur"
  bioModerator?: string | null; // non renvoyé par le back pour l'instant
}

/** Réponse de POST /intervenants/import (ImportExcelResultDto côté back). */
export interface ImportIntervenantsResult {
  totalRows: number;
  imported: number;
  ignored: number;
  errors: string[];
}

@Injectable({
  providedIn: 'root',
})
export class ApplicationSimplifyService {
  constructor(private http: HttpClient) {}

  createWebinaire(dto: WebinarSimplifyDto) {
    return this.http.post<CustomResponse>(
      `${config.apiBaseUrl}/webinaireSimplify/createWebinaire`,
      dto,
    );
  }

  updateWebinaire(dto: WebinarSimplifyDto, id: number) {
    return this.http.put<CustomResponse>(
      `${config.apiBaseUrl}/webinaireSimplify/` + id,
      dto,
    );
  }

  createModerator(webinaireId: number, dto: any): Observable<any> {
    return this.http.post(
      `${config.apiBaseUrl}/moderatorSimplify/createModerator/${webinaireId}`,
      dto,
    );
  }

  getModeratorsByWebinarId(webinarId: number): Observable<any> {
    return this.http.get(
      `${config.apiBaseUrl}/moderatorSimplify/byWebinar/${webinarId}`,
    );
  }

  addAppel(form: any, idWebinars: number[]): Observable<any> {
    let queryParams = new HttpParams();
    idWebinars.forEach((id) => {
      queryParams = queryParams.append('idWebinar', id.toString());
    });

    return this.http.post<any>(
      `${config.openBaseUrl}/opencontroller/createCandidature`,
      form,
      { params: queryParams },
    );
  }
  addCandidature(form: any): Observable<any> {
    return this.http.post<any>(
      `${config.apiBaseUrl}/applicationWebinaire/createApplicationWebinaire`,
      form,
    );
  }
  getGroupedWebinars(): Observable<any> {
    return this.http.get<any>(
      `${config.apiBaseUrl}/webinaireSimplify/grouped-by-cohorte`,
    );
  }

  getwebinarByCohorte(cohorte: string): Observable<any> {
    const queryParams = new HttpParams().set('cohorte', cohorte);

    return this.http.get<any>(
      `${config.openBaseUrl}/opencontroller/webinars-by-cohorte`,
      { params: queryParams },
    );
  }
 getParticipantWebinars(
  page: number,
  size: number,
  gender?: string,
  cohorte?: string,
  theme?: string,
  search?: string,
  dateDebut?: string,
  dateFin?: string,
): Observable<PaginatedResponse<any>> {
  let params = new HttpParams()
    .set('page', page.toString())
    .set('size', size.toString());

  if (gender) params = params.set('gender', gender);
  if (cohorte) params = params.set('cohorte', cohorte);
  if (theme) params = params.set('theme', theme);
  if (search) params = params.set('search', search);
  if (dateDebut) params = params.set('dateDebut', dateDebut);
  if (dateFin) params = params.set('dateFin', dateFin);

  return this.http.get<PaginatedResponse<any>>(
    `${config.apiBaseUrl}/webinars/participants`,
    { params },
  );
}

 getListWebinarThemes(
  dateDebut?: string,
  dateFin?: string,
): Observable<string[]> {
  let params = new HttpParams();
  if (dateDebut) params = params.set('dateDebut', dateDebut);
  if (dateFin) params = params.set('dateFin', dateFin);

  return this.http.get<string[]>(
    `${config.apiBaseUrl}/webinars/participants/webinars/themes`,
    { params },
  );
}

  getlistcohortWebinars(): Observable<any> {
    return this.http.get<any>(
      `${config.apiBaseUrl}/webinars/participants/cohortes`,
    );
  }
  public toggleApplicationLaunchWebinaire(id: number): Observable<any> {
    return this.http.patch<any>(
      `${config.apiBaseUrl}/applicationWebinaire/toggleApplicationLaunch/${id}`,
      null,
    );
  }
  public getApplicationWebinaireById(id: number): Observable<any> {
    return this.http.get<any>(
      `${config.apiBaseUrl}/applicationWebinaire/getApplicationWebinaire/${id}`,
    );
  }

  public updateApplicationWebinaire(id: number, data: any): Observable<any> {
    return this.http.put<any>(
      `${config.apiBaseUrl}/applicationWebinaire/updateApplicationWebinaire/${id}`,
      data,
    );
  }
  getRecentCohorte(): Observable<string[]> {
    return this.http.get<string[]>(
      `${config.apiBaseUrl}/webinaireSimplify/recentCohorte`,
    );
  }
  getWebinarTypeOptions(): Observable<string[]> {
    return this.http.get<string[]>(
      `${config.apiBaseUrl}/webinaireSimplify/webinar-type-options`,
    );
  }

  // ═══════════════════ Annuaire des intervenants (table Intervenants) ═══════════════════

  // GET {apiBaseUrl}/intervenants?role=MODERATOR|PANELISTE
  getSpeakersByRole(
    role: IntervenantRole | string,
  ): Observable<IntervenantResponse[]> {
    const params = new HttpParams().set('role', role);
    return this.http.get<IntervenantResponse[]>(
      `${config.apiBaseUrl}/intervenants`,
      { params },
    );
  }

  // POST {apiBaseUrl}/intervenants — création unitaire (avec civilité)
  createIntervenant(dto: IntervenantRequest): Observable<IntervenantResponse> {
    return this.http.post<IntervenantResponse>(
      `${config.apiBaseUrl}/intervenants`,
      dto,
    );
  }

  // POST {apiBaseUrl}/intervenants/import — import Excel (.xlsx)
  importIntervenants(file: File): Observable<ImportIntervenantsResult> {
    const formData = new FormData();
    formData.append('file', file);
    return this.http.post<ImportIntervenantsResult>(
      `${config.apiBaseUrl}/intervenants/import`,
      formData,
    );
  }
}
